/**
 * Tokenize a string containing inline mentions of the form
 * `[@DisplayName](kind:id)` into a sequence of plain-text and mention segments.
 */

export type Segment =
  | { type: 'text'; value: string }
  | { type: 'mention'; kind: string; id: string; label: string; raw: string };

// Matches `[@Label](kind:id)`. `kind` must be lower-case alpha/underscore.
// `label` may not contain `]`; `id` may not contain `)`.
const MENTION_RE = /\[@([^\]]+)\]\(([a-z_]+):([^)]+)\)/g;

export function parseMentions(text: string): Segment[] {
  if (!text) return [];
  const segments: Segment[] = [];
  let cursor = 0;
  // `matchAll`-like iteration — we need raw match length per iteration.
  MENTION_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MENTION_RE.exec(text)) !== null) {
    const [raw, label, kind, id] = m;
    if (m.index > cursor) {
      segments.push({ type: 'text', value: text.slice(cursor, m.index) });
    }
    segments.push({ type: 'mention', kind, id, label, raw });
    cursor = m.index + raw.length;
  }
  if (cursor < text.length) {
    segments.push({ type: 'text', value: text.slice(cursor) });
  }
  return segments;
}

/** Build the inline mention token for insertion. */
export function formatMention(kind: string, id: string, label: string): string {
  return `[@${label}](${kind}:${id})`;
}

/**
 * Given the caret position within a plain-text value, find an active mention
 * query of the form `@foo` ending at the caret. Returns null if none.
 *
 * A query is active when:
 * - the `@` is preceded by start-of-string or whitespace,
 * - every character between `@` and the caret is a "word" char (no spaces).
 */
export interface ActiveQuery {
  /** Index of the `@` trigger character. */
  start: number;
  /** Caret position (exclusive end of the query). */
  end: number;
  /** The query string after the `@`, not including `@`. */
  query: string;
}

export function detectActiveQuery(value: string, caret: number): ActiveQuery | null {
  if (caret <= 0) return null;
  // Walk backward from the caret looking for `@`. Abort on whitespace or if we
  // wander past a plausible mention length (64 chars).
  const max = Math.max(0, caret - 64);
  for (let i = caret - 1; i >= max; i--) {
    const ch = value[i];
    if (ch === '@') {
      // `@` must be at start or preceded by whitespace/punctuation.
      if (i === 0 || /\s/.test(value[i - 1])) {
        // Don't trigger inside an existing inline mention: if we're right
        // after a `[@…`, skip.
        if (i > 0 && value[i - 1] === '[') return null;
        const query = value.slice(i + 1, caret);
        return { start: i, end: caret, query };
      }
      return null;
    }
    if (/\s/.test(ch)) {
      return null;
    }
  }
  return null;
}
